const MAX_RGB_VALUE = 255;

/**
 * Converts n to 2-digit hex value where -255 <= n <= 255 values to 6-digit hex code
 * @param {String|number} c
 * @returns {String} 2-digit hex code representation of color
 */
const componentToHex = c => {
  const digit = Math.abs(parseInt(c, 10));
  if (digit <= MAX_RGB_VALUE) {
    const hex = digit.toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  }
  return '00';
};

/**
 * Converts RGB values to 6-digit hex code
 * @param {String|number} r
 * @param {String|number} g
 * @param {String|number} b
 * @returns {String} 6-digit hex code representation of color
 */
export const rgbToHex = (r, g, b) => `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;

/**
 * Prepends '#' symbol to the passed string (if it's missing).
 *
 * @param {string} hexColor hex color string, may OR may not start with '#' (e.g. '#2070e8' or '2070e8').
 * @returns {string} hex color string that starts with '#'.
 */
export const prependSharp = hexColor => (!hexColor || hexColor.startsWith('#') ? hexColor : `#${hexColor}`);

/**
 * Converts hex color code to RGB values.
 * Supports both shorthand (e.g. "03F" or "#03F") and full (e.g. "0033FF" or "#0033FF") formats.
 *
 * @param {string} hexCode hex color string, with or without '#' prefix
 * @returns {Object|null} object with r, g, b properties (0-255), or null if invalid format
 */
export const hexToRgb = hexCode => {
  if (!hexCode || typeof hexCode !== 'string') {
    return null;
  }

  const SHORTHAND_HEX_REGEX = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const expandedHex = hexCode.replace(SHORTHAND_HEX_REGEX, (match, r, g, b) => r + r + g + g + b + b);

  const FULL_HEX_REGEX = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;
  const match = FULL_HEX_REGEX.exec(expandedHex);

  if (!match) {
    return null;
  }

  return {
    r: parseInt(match[1], 16),
    g: parseInt(match[2], 16),
    b: parseInt(match[3], 16),
  };
};
